import { useQuery, queryOptions } from '@tanstack/vue-query';
import { computed } from 'vue';
import httpClient from '../httpClient';
import { auth0 } from '@/main';
import { useTenantStore } from '@/stores/tenant';
import type { Tenant } from '@/types';

/**
 * Lists the tenants the current user may act on (via the scribe-backend, which
 * resolves them from manta over M2M). Also keeps the tenant store consistent:
 * drops a selection the user no longer has access to and auto-selects the first
 * available tenant when none is chosen.
 */
export const useMineTenantsQuery = () => {
  const fetcher = async (): Promise<Tenant[]> => {
    const { data } = await httpClient.get<Tenant[]>('/v1/tenants/mine');

    const tenantStore = useTenantStore();
    const current = tenantStore.selectedTenantId;
    if (current && !data.some((t) => t.id === current)) {
      tenantStore.removeSelectedTenant();
    }
    if (!tenantStore.selectedTenantId && data.length > 0) {
      tenantStore.setSelectedTenant(data[0].id);
    }

    return data;
  };

  // Lives in the always-mounted sidebar, so gate on auth to avoid a tokenless
  // request that 401s during initial load.
  const enabled = computed(
    () =>
      import.meta.env.VITE_DISABLE_AUTH === 'true' ||
      auth0.isAuthenticated.value,
  );

  return useQuery(
    queryOptions({
      queryKey: ['tenantsMine'],
      queryFn: fetcher,
      enabled,
    }),
  );
};
